import HttpClient from 'src/features/http';
import {
  LoginResponse,
  RegisterResponse,
  AuthServiceInterface,
  LoginRequestParams,
  RegisterRequestParams,
  LogoutRequestParams,
  LogoutResponse,
  ResetPasswordRequestParams,
  ResetPasswordResponse,
  RegisterDeviceIdParams,
  RegisterDeviceIdResponse,
  GetUserByIdParams,
  GetSocialProfileParams,
  GetUserByIdResponse,
  ApiError,
  CheckUserExistsParams,
  CheckUserExistsResponse,
} from './authService.types';
import { backendUrl } from 'src/config';
import { inject, injectable } from 'inversify';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  signInWithCredential,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { getApp } from 'firebase/app';
import { AxiosError } from 'axios';

const ERROR_MESSAGES: { [x: string]: string } = {
  phone_number: 'El numero de teléfono ya está en uso',
  email: 'El correo electrónico ya está en uso',
};

@injectable()
class AuthService implements AuthServiceInterface {
  @inject(HttpClient) private client!: HttpClient;

  async login(params: LoginRequestParams) {
    return await this.client
      .getClient()
      .post<LoginResponse>('/users/login/', params, { baseURL: backendUrl });
  }

  async register(params: RegisterRequestParams) {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => !!value)
    );

    try {
      const response = await this.client
        .getClient()
        .post<RegisterResponse>('/users/signup/', filteredParams, {
          baseURL: backendUrl,
        });
      return response;
    } catch (error) {
      const errors = (error as AxiosError).response?.data as ApiError;

      const errorMessageKeys = Object.keys(errors);
      const errorMessage = ERROR_MESSAGES[errorMessageKeys[0]];

      throw new Error(errorMessage ?? errors ?? (error as AxiosError).message);
    }
  }

  async registerJwt(params: { token: string }) {
    return await this.client
      .getClient()
      .post<RegisterResponse>(
        '/jwt/register/',
        { token: params.token },
        { baseURL: backendUrl }
      );
  }

  async logout(params: LogoutRequestParams) {
    return await this.client.getClient().get<LogoutResponse>('/users/logout/', {
      baseURL: backendUrl,
      headers: {
        Authorization: `Token ${params.token}`,
        'Content-Type': 'application/json',
      },
    });
  }

  async resetPassword(params: ResetPasswordRequestParams) {
    return await this.client
      .getClient()
      .post<ResetPasswordResponse>('/password-reset/', params, {
        baseURL: backendUrl,
      });
  }

  async registerDeviceId(params: RegisterDeviceIdParams) {
    return await this.client.getClient().post<RegisterDeviceIdResponse>(
      `/users/${params.user_id}/devices/`,
      { registration_id: params.device_id },
      {
        baseURL: backendUrl,
        headers: {
          Authorization: `Token ${params.token}`,
          'Content-Type': 'application/json',
        },
      }
    );
  }

  async getUserById(params: GetUserByIdParams) {
    return await this.client
      .getClient()
      .get<GetUserByIdResponse>(`/users/${params.user_pk}/`, {
        baseURL: backendUrl,
        headers: {
          Authorization: `Token ${params.token}`,
          'Content-Type': 'application/json',
        },
      });
  }

  async getSocialProfile(params: GetSocialProfileParams) {
    const provider = params.provider;

    const signInMethods = {
      facebook: this.signInWithFacebook,
      google: this.signInWithGoogle,
      apple: this.signInWithApple,
    };

    return await signInMethods[provider]();
  }

  private async signInWithFacebook() {
    // 1. Create credentials on the native layer
    const result = await FirebaseAuthentication.signInWithFacebook();
    // 2. Sign in on the web layer using the access token
    const credential = FacebookAuthProvider.credential(
      result.credential?.accessToken as string
    );
    const auth = await this.getFirebaseAuth();
    return await signInWithCredential(auth, credential);
  }

  private async signInWithGoogle() {
    // 1. Create credentials on the native layer
    const result = await FirebaseAuthentication.signInWithGoogle();
    // 2. Sign in on the web layer using the id token
    const credential = GoogleAuthProvider.credential(
      result.credential?.idToken
    );
    const auth = await this.getFirebaseAuth();
    return await signInWithCredential(auth, credential);
  }

  private async signInWithApple() {
    // 1. Create credentials on the native layer
    const result = await FirebaseAuthentication.signInWithApple({
      skipNativeAuth: true,
    });
    // 2. Sign in on the web layer using the id token and nonce
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({
      idToken: result.credential?.idToken,
      rawNonce: result.credential?.nonce,
    });
    const auth = await this.getFirebaseAuth();
    return await signInWithCredential(auth, credential);
  }

  private async getFirebaseAuth() {
    if (Capacitor.isNativePlatform()) {
      return initializeAuth(getApp(), {
        persistence: indexedDBLocalPersistence,
      });
    } else {
      return getAuth();
    }
  }

  async getDeviceIpAddress() {
    return await this.client
      .getClient()
      .get('https://api.ipify.org?format=json');
  }

  async checkUserExists({ phone_number }: CheckUserExistsParams) {
    return await this.client
      .getClient()
      .post<CheckUserExistsResponse>(
        '/users/check-phone-exists/',
        { phone_number },
        { baseURL: backendUrl }
      );
  }
}

export default AuthService;
